import { useEffect, useRef, useState } from 'react';
import { useAudioCapture } from '../hooks/useAudioCapture';
import { useBrowserSpeechRecognition } from '../hooks/useBrowserSpeechRecognition';
import { useLocalWhisperModel } from '../hooks/useLocalWhisperModel';
import { parseTranscript, formatClockTime } from '../utils/transcript';
import RecordingControls from './RecordingControls';
import MarkdownEditor from './MarkdownEditor';

export default function NoteEditor({ note, api, onSave, onError }) {
  const [title, setTitle] = useState(note.title);
  const [content, setContent] = useState(note.content);
  const [transcriptEntries, setTranscriptEntries] = useState(() => parseTranscript(note.transcript));
  const [engine, setEngine] = useState('local'); // 'local' (in-browser Whisper) | 'browser' (Web Speech API)
  const saveTimer = useRef(null);
  const pendingSave = useRef(null);
  useEffect(() => () => {
    clearTimeout(saveTimer.current);
    if (pendingSave.current) onSave(note.id, pendingSave.current);
  }, [note.id]);
  const recordingStartRef = useRef(null);
  const transcriptScrollRef = useRef(null);

  useEffect(() => {
    setTitle(note.title);
    setContent(note.content);
    setTranscriptEntries(parseTranscript(note.transcript));
  }, [note.id]);

  useEffect(() => {
    const el = transcriptScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [transcriptEntries]);

  const scheduleSave = (patch) => {
    pendingSave.current = patch;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      pendingSave.current = null;
      onSave(note.id, patch);
    }, 600);
  };

  const handleTitleChange = (e) => {
    const value = e.target.value;
    setTitle(value);
    scheduleSave({ title: value, content });
  };

  const handleContentChange = (value) => {
    setContent(value);
    scheduleSave({ title, content: value });
  };

  const handleTranscript = async (text) => {
    const elapsedMs = recordingStartRef.current != null ? Date.now() - recordingStartRef.current : null;
    setTranscriptEntries((prev) => [...prev, { text, elapsedMs, at: new Date().toISOString() }]);
    try {
      const updated = await api.appendTranscript(note.id, text, elapsedMs);
      setTranscriptEntries(parseTranscript(updated.transcript));
    } catch (err) {
      onError(`Could not save transcript: ${err.message}`);
    }
  };

  const [quality, setQuality] = useState('balanced');
  const localModel = useLocalWhisperModel(quality);
  const localCapture = useAudioCapture({
    onTranscript: handleTranscript,
    transcribeChunk: localModel.transcribeChunk,
  });
  const browserCapture = useBrowserSpeechRecognition({ onTranscript: handleTranscript });

  const activeCapture = engine === 'browser' ? browserCapture : localCapture;

  // the Web Speech API only listens to the default mic, so ignore the requested source mode
  const handleStart = (mode) => {
    recordingStartRef.current = Date.now();
    if (engine === 'browser') browserCapture.start();
    else localCapture.start(mode);
  };

  const handleStop = () => {
    if (engine === 'browser') browserCapture.stop();
    else localCapture.stop();
  };

  const insertTranscriptIntoNote = () => {
    const formatted = transcriptEntries
      .map((entry) => (entry.at ? `[${formatClockTime(entry.at)}] ${entry.text}` : entry.text))
      .join('\n');
    const merged = content ? `${content}\n\n${formatted}` : formatted;
    setContent(merged);
    scheduleSave({ title, content: merged });
  };

  const handleClearTranscript = async () => {
    if (transcriptEntries.length === 0) return;
    if (!window.confirm('Clear the live transcript for this note? This cannot be undone.')) return;
    try {
      await api.updateNote(note.id, { transcript: '' });
      setTranscriptEntries([]);
    } catch (err) {
      onError(`Could not clear transcript: ${err.message}`);
    }
  };

  return (
    <div className="note-editor">
      <input
        aria-label="Note title"
        className="note-title-input"
        value={title}
        onChange={handleTitleChange}
        placeholder="Note title"
      />

      <RecordingControls
        status={activeCapture.status}
        sourceLabel={engine === 'browser' ? 'browser mic' : activeCapture.sourceLabel}
        error={activeCapture.error}
        onStart={handleStart}
        onStop={handleStop}
        engine={engine}
        onEngineChange={setEngine}
        browserEngineSupported={browserCapture.isSupported}
        localModelStatus={localModel.modelStatus}
        localModelProgress={localModel.progress}
        quality={quality}
        onQualityChange={setQuality}
      />

      <MarkdownEditor key={note.id} value={content} onChange={handleContentChange} />

      <div className="transcript-panel">
        <div className="transcript-panel-header">
          <h3>Live transcript</h3>
          {transcriptEntries.length > 0 && (
            <div className="transcript-panel-actions">
              <button className="btn-link" onClick={insertTranscriptIntoNote}>
                Insert into note
              </button>
              <button className="btn-link btn-link-danger" onClick={handleClearTranscript}>
                Clear transcript
              </button>
            </div>
          )}
        </div>
        {transcriptEntries.length > 0 ? (
          <ol className="transcript-list" ref={transcriptScrollRef}>
            {transcriptEntries.map((entry, i) => (
              <li key={i} className="transcript-entry">
                {entry.at && (
                  <span className="transcript-timestamp">{formatClockTime(entry.at)}</span>
                )}
                <span className="transcript-entry-text">{entry.text}</span>
              </li>
            ))}
          </ol>
        ) : (
          <span className="transcript-placeholder">
            Transcribed speech from your microphone or shared tab/system audio will appear here,
            broken into timestamped segments.
          </span>
        )}
      </div>
    </div>
  );
}
